import { createContext, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, X } from "lucide-react";
import { fileUrl } from "../lib/api";

export function Sheet({ open, onClose, title, children, testid = "sheet", full }) {
  useEffect(() => {
    if (!open) return undefined;
    const o = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = o; };
  }, [open]);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center" data-testid={testid}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm fade-in" onClick={onClose} />
      <div className={`sheet-panel relative w-full max-w-md ${full ? "h-[94vh]" : "max-h-[90vh]"} overflow-y-auto rounded-t-[28px] pb-safe slide-up`}>
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 pt-4 pb-3 bg-oh-surface">
          <h3 className="display text-2xl">{title}</h3>
          <button className="icon-btn" onClick={onClose} data-testid={`${testid}-close`} aria-label="Close"><X size={18} /></button>
        </div>
        <div className="px-5 pb-6">{children}</div>
      </div>
    </div>,
    document.body
  );
}

const ConfirmCtx = createContext(null);
export function ConfirmProvider({ children }) {
  const [st, setSt] = useState(null);
  const confirm = (o) => new Promise((resolve) => setSt({ ...o, resolve }));
  const done = (v) => { st.resolve(v); setSt(null); };
  const actions = st?.actions || [{ label: st?.okLabel || "Confirm", value: true, danger: st?.danger }];
  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {st && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-6" data-testid="confirm-dialog">
          <div className="absolute inset-0 bg-black/70 fade-in" onClick={() => done(null)} />
          <div className="card relative w-full max-w-sm p-5 pop-in">
            <h4 className="display text-2xl mb-1">{st.title}</h4>
            {st.body && <p className="text-oh-text2 text-sm mb-4">{st.body}</p>}
            <div className="flex flex-col gap-2 mt-3">
              {actions.map((a, i) => (
                <button key={i} className={a.danger ? "btn btn-danger" : "btn"} onClick={() => done(a.value)} data-testid={`confirm-action-${i}`}>{a.label}</button>
              ))}
              <button className="btn btn-ghost" onClick={() => done(null)} data-testid="confirm-cancel">Cancel</button>
            </div>
          </div>
        </div>, document.body)}
    </ConfirmCtx.Provider>
  );
}
export const useConfirm = () => useContext(ConfirmCtx);

export const Empty = ({ icon: Icon, text, testid = "empty-state", children }) => (
  <div className="flex flex-col items-center justify-center text-center py-8 px-4 text-oh-text2" data-testid={testid}>
    {Icon && <Icon size={26} strokeWidth={1.4} className="mb-2 opacity-70" />}
    <p className="text-sm">{text}</p>
    {children}
  </div>
);

export const Tabs = ({ value, onChange, options, testid = "tabs", small }) => (
  <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1" data-no-swipe data-testid={testid}>
    {options.map(([v, l]) => (
      <button key={v} onClick={() => onChange(v)} className={`chip ${small ? "chip-sm" : ""} ${value === v ? "chip-on" : ""}`} data-testid={`${testid}-${v}`}>{l}</button>
    ))}
  </div>
);

export const Field = ({ label, children, hint }) => (
  <label className="block mb-3">
    <span className="label-xs block mb-1.5">{label}</span>
    {children}
    {hint && <span className="text-xs text-oh-text2 mt-1 block">{hint}</span>}
  </label>
);

export const Seg = ({ value, onChange, options, testid, disabled = [] }) => (
  <div className="seg" data-testid={testid}>
    {options.map(([v, l]) => (
      <button type="button" key={v} disabled={disabled.includes(v)} onClick={() => onChange(v)} className={value === v ? "on" : ""} data-testid={`${testid}-${v}`}>{l}</button>
    ))}
  </div>
);

export function PageHead({ title, right, sub, testid = "page-head" }) {
  const nav = useNavigate();
  const back = () => (window.history.state?.idx > 0 ? nav(-1) : nav("/home"));
  return (
    <div className="flex items-end justify-between gap-3 mb-5 rise" data-testid={testid}>
      <div className="flex items-center gap-2 min-w-0">
        <button className="icon-btn -ml-1" onClick={back} data-testid="back-button" aria-label="Back"><ChevronLeft size={20} /></button>
        <div className="min-w-0">
          <h1 className="display text-4xl leading-none truncate">{title}</h1>
          {sub && <p className="text-oh-text2 text-xs mt-1">{sub}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">{right}</div>
    </div>
  );
}

export const Img = ({ id, thumb, className = "", alt = "", ...p }) =>
  id ? <img src={fileUrl(id, thumb)} alt={alt} loading="lazy" className={`object-cover ${className}`} {...p} /> : <div className={`bg-oh-widget ${className}`} />;

export const Badge = ({ children, tone = "muted", testid }) => <span className={`badge badge-${tone}`} data-testid={testid}>{children}</span>;

export const Counter = ({ value, max }) => <span className={`text-xs ${value.length > max ? "text-red-400" : "text-oh-text2"}`}>{value.length}/{max}</span>;
